import type { IncomingMessage } from 'http';

import { publicConfig } from '@public-config';

import { WorkspaceHelpers } from 'helpers/WorkspaceHelpers';

import { ApiService } from 'services/ApiService';
import type { WorkspaceConfigState } from 'state/workspace-config';

import type { Workspace } from '@/payload-types';

type RequestWithWorkspaceConfig = IncomingMessage & { workspaceConfig?: WorkspaceConfigState };

// Loads the workspace settings from the `workspaces` collection. Usable on the server
// (getInitialProps / getServerSideProps / route handlers) and in the browser.
const WorkspaceService = {
    // Loads the config for the host of a server request once; `_app` and a page's getServerSideProps
    // run in the same request and share the result instead of fetching twice.
    getRequestConfig: async (req: IncomingMessage): Promise<WorkspaceConfigState | undefined> => {
        const request = req as RequestWithWorkspaceConfig;

        if (!('workspaceConfig' in request)) {
            request.workspaceConfig = await WorkspaceService.getConfig(req.headers.host);
        }

        return request.workspaceConfig;
    },

    // Accepts a workspace key, host or origin. Resolves to `undefined` when it is not a configured
    // workspace or its document does not exist (yet).
    getConfig: async (keyOrHost: string | null | undefined): Promise<WorkspaceConfigState | undefined> => {
        const key = WorkspaceHelpers.getConfig(keyOrHost)?.key;

        if (!key) {
            return undefined;
        }

        return ApiService.get(`${publicConfig.urls.api}/workspaces/public/${encodeURIComponent(key)}`, {
            hideConsoleError: true,
        });
    },

    get: async (id: string): Promise<Workspace | undefined> => {
        return ApiService.get(`${publicConfig.urls.api}/workspaces/${encodeURIComponent(id)}?depth=0`);
    },
};

export { WorkspaceService };
